import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../../components/SEOHead';
import HormuzHubNav from '../../../components/HormuzHubNav';
import {
  getOrganizationSchema,
  getBreadcrumbSchema,
} from '../../../utils/seoSchemas';
import { Navigation, Globe, BarChart3, PieChart as PieChartIcon } from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';

const geoUrl = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const HormuzShippingRoutes = () => {
  const title = 'Strait of Hormuz Shipping Routes | Tanker Paths, Alternatives & Costs';
  const description =
    'How shipping routes through the Strait of Hormuz connect the Persian Gulf to global markets, and what happens to voyage times, costs, and risk when flows are diverted to alternative routes.';

  const jsonLd = [
    getOrganizationSchema(),
    getBreadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Strait of Hormuz', path: '/strait-of-hormuz' },
      { name: 'Shipping Routes', path: '/strait-of-hormuz/shipping-routes' },
    ]),
  ];

  const [selectedRoute, setSelectedRoute] = useState(null);

  const routes = [
    {
      destination: 'Asia-Pacific',
      countries: ['China', 'Japan', 'South Korea', 'India', 'Singapore'],
      volume: 60,
      description: 'Primary destination for Persian Gulf oil exports',
      color: '#3b82f6',
      path: [
        [56, 26], // Strait of Hormuz
        [65, 25],
        [75, 20],
        [90, 15],
        [105, 10],
        [120, 5],
        [135, 35], // Japan
        [127, 36], // South Korea
        [104, 35], // China
        [77, 20], // India
        [103, 1], // Singapore
      ],
      destinationCoords: [
        [139, 36], // Japan
        [127, 36], // South Korea
        [104, 35], // China
        [77, 20], // India
        [103, 1], // Singapore
      ],
    },
    {
      destination: 'Europe',
      countries: ['UK', 'France', 'Germany', 'Italy', 'Spain'],
      volume: 20,
      description: 'European refineries depend on Hormuz transit',
      color: '#10b981',
      path: [
        [56, 26], // Strait of Hormuz
        [50, 30],
        [30, 35],
        [10, 40],
        [-5, 50],
        [-2, 51], // UK
        [2, 49], // France
        [9, 51], // Germany
        [12, 42], // Italy
        [-3, 40], // Spain
      ],
      destinationCoords: [
        [-2, 51], // UK
        [2, 49], // France
        [9, 51], // Germany
        [12, 42], // Italy
        [-3, 40], // Spain
      ],
    },
    {
      destination: 'Americas',
      countries: ['USA', 'Brazil', 'Canada'],
      volume: 15,
      description: 'Trans-Atlantic shipping routes',
      color: '#f59e0b',
      path: [
        [56, 26], // Strait of Hormuz
        [50, 30],
        [30, 35],
        [0, 40],
        [-30, 45],
        [-70, 40], // USA East Coast
        [-74, 40], // New York
        [-46, -23], // Brazil
        [-75, 45], // Canada
      ],
      destinationCoords: [
        [-74, 40], // USA
        [-46, -23], // Brazil
        [-75, 45], // Canada
      ],
    },
    {
      destination: 'Africa',
      countries: ['South Africa', 'Egypt', 'Morocco'],
      volume: 5,
      description: 'Regional distribution and refining',
      color: '#8b5cf6',
      path: [
        [56, 26], // Strait of Hormuz
        [50, 25],
        [30, 20],
        [20, 10],
        [18, -34], // South Africa
        [31, 30], // Egypt
        [-7, 33], // Morocco
      ],
      destinationCoords: [
        [18, -34], // South Africa
        [31, 30], // Egypt
        [-7, 33], // Morocco
      ],
    },
  ];

  // Chart data
  const routePieData = routes.map((r) => ({
    name: r.destination,
    value: r.volume,
    color: r.color,
  }));

  const routeBarData = routes.map((r) => ({
    name: r.destination,
    volume: r.volume,
    countries: r.countries.length,
  }));

  return (
    <article className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEOHead
        title={title}
        description={description}
        path="/strait-of-hormuz/shipping-routes"
        keywords="Hormuz shipping routes, tanker routes, Persian Gulf shipping, alternative routes"
        jsonLd={jsonLd}
        image="/api/og-image/hormuz-overview.png"
        imageAlt="Strait of Hormuz Shipping Routes - Tanker routes and navigation"
      />

      <HormuzHubNav />

      {/* A. Overview */}
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-on-surface mb-2">
          Shipping Routes Through the Strait of Hormuz
        </h1>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-2">
          Last updated: March 2026 · This page focuses on typical tanker paths and high‑level cost
          implications rather than live vessel tracking.
        </p>
        <p className="text-sm text-slate-600 dark:text-on-surface-variant leading-relaxed">
          Most crude and product cargoes leaving the Persian Gulf pass through the Strait of Hormuz
          before branching toward Asia, Europe, the Americas, or Africa. When that main corridor is
          constrained, ships may need to queue, slow down under escort, or divert to much longer
          routes. Even if total global oil supply remains adequate, these detours can add days to
          voyages and materially raise freight and insurance costs.
        </p>
      </header>

      {/* World Map with Routes */}
      <section className="mb-10">
        <div className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
          <h2 className="text-2xl font-semibold text-slate-900 dark:text-on-surface mb-4 flex items-center">
            <Globe className="w-6 h-6 mr-2 text-primary-600" />
            Global Shipping Routes Map
          </h2>
          <div className="h-96 w-full rounded-lg overflow-hidden bg-slate-100 dark:bg-surface-container-low relative">
            <ComposableMap projectionConfig={{ scale: 150 }}>
              <Geographies geography={geoUrl}>
                {({ geographies }) =>
                  geographies.map((geo) => (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill="#e5e7eb"
                      stroke="#ffffff"
                      strokeWidth={0.5}
                      style={{
                        default: { outline: 'none' },
                        hover: { outline: 'none', fill: '#d1d5db' },
                        pressed: { outline: 'none' },
                      }}
                    />
                  ))
                }
              </Geographies>
              
              {/* Markers for destinations */}
              {routes.map((route, routeIdx) =>
                route.destinationCoords.map((coords, coordIdx) => (
                  <Marker key={`${routeIdx}-${coordIdx}`} coordinates={coords}>
                    <g>
                      <circle
                        r={selectedRoute === routeIdx ? 6 : 4}
                        fill={route.color}
                        stroke="#fff"
                        strokeWidth={2}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedRoute(selectedRoute === routeIdx ? null : routeIdx)}
                        opacity={selectedRoute === null || selectedRoute === routeIdx ? 1 : 0.4}
                      />
                      {selectedRoute === routeIdx && (
                        <text
                          textAnchor="middle"
                          y={-10}
                          style={{ fontFamily: 'system-ui', fill: route.color, fontSize: '10px', fontWeight: 'bold' }}
                        >
                          {route.destination}
                        </text>
                      )}
                    </g>
                  </Marker>
                ))
              )}
              
              {/* Strait of Hormuz marker */}
              <Marker coordinates={[56, 26]}>
                <circle r={6} fill="#ef4444" stroke="#fff" strokeWidth={2} />
                <text
                  textAnchor="middle"
                  y={-12}
                  style={{ fontFamily: 'system-ui', fill: '#ef4444', fontSize: '10px', fontWeight: 'bold' }}
                >
                  Hormuz
                </text>
              </Marker>
            </ComposableMap>
          </div>
          <div className="mt-4 flex items-center justify-center gap-4 flex-wrap">
            {routes.map((route, idx) => (
              <button
                key={idx}
                onClick={() => setSelectedRoute(selectedRoute === idx ? null : idx)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  selectedRoute === idx
                    ? 'ring-2 ring-offset-2'
                    : ''
                }`}
                style={{
                  backgroundColor: selectedRoute === idx ? route.color : `${route.color}40`,
                  color: selectedRoute === idx ? '#fff' : '#374151',
                  ringColor: route.color,
                }}
              >
                {route.destination} ({route.volume}%)
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Charts Section */}
      <section className="mb-10">
        <h2 className="text-2xl font-semibold text-slate-900 dark:text-on-surface mb-4 flex items-center">
          <BarChart3 className="w-6 h-6 mr-2 text-primary-600" />
          Route Statistics
        </h2>
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Pie Chart */}
          <div className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-on-surface mb-4 flex items-center">
              <PieChartIcon className="w-5 h-5 mr-2 text-primary-600" />
              Volume Distribution
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={routePieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {routePieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Bar Chart */}
          <div className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-on-surface mb-4">
              Route Volumes & Destinations
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={routeBarData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="volume" name="Volume %" fill="#3b82f6">
                  {routeBarData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={routes[index].color} />
                  ))}
                </Bar>
                <Bar dataKey="countries" name="Destinations" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Route Overview */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {routes.map((route, idx) => (
          <div
            key={idx}
            className={`rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6 transition-all cursor-pointer ${
              selectedRoute === idx ? 'ring-2 ring-primary-500' : ''
            }`}
            onClick={() => setSelectedRoute(selectedRoute === idx ? null : idx)}
            style={{
              borderColor: selectedRoute === idx ? route.color : undefined,
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-on-surface flex items-center">
                <Navigation className="w-5 h-5 mr-2" style={{ color: route.color }} />
                {route.destination}
              </h3>
              <span className="text-sm font-medium" style={{ color: route.color }}>
                {route.volume}%
              </span>
            </div>
            <p className="text-sm text-slate-600 dark:text-on-surface-variant mb-3">{route.description}</p>
            <div className="flex flex-wrap gap-2">
              {route.countries.map((country) => (
                <span
                  key={country}
                  className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-xs rounded text-slate-700 dark:text-on-surface-variant"
                >
                  {country}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Alternative Routes & Comparative Table */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-6 mb-8">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-3">
          Alternative Routes If Hormuz Is Constrained
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-3">
          When risk levels rise, some cargoes can be re‑routed around Hormuz. These work‑arounds
          reduce exposure to a single chokepoint but add distance, days, and cost. The table
          summarises typical order‑of‑magnitude differences.
        </p>
        <div className="overflow-x-auto overflow-hidden rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container mb-3">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-100 dark:bg-surface-container-low">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200">
                  Route
                </th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200">
                  Extra distance (approx.)
                </th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200">
                  Extra sailing days
                </th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200">
                  Cost impact (illustrative)
                </th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200">
                  Typical use case
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              <tr>
                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">Bab el‑Mandeb &amp; Suez</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">≈ +2,000 nautical miles</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">≈ +5–7 days</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Higher canal dues and bunker costs; per‑barrel freight can increase materially on
                  some routes.
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Crude and products bound for Europe and the Mediterranean when Hormuz risk is
                  elevated but Suez remains available.
                </td>
              </tr>
              <tr>
                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">Cape of Good Hope</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">≈ +3,500+ nautical miles</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">≈ +10–15 days</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Significantly higher voyage costs; in tight markets this can add several dollars
                  per barrel once spread across the cargo.
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Diversion option if either Suez or Red Sea routes face simultaneous disruption.
                </td>
              </tr>
              <tr>
                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">Pipelines &amp; regional routes</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">Route‑specific</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">Can reduce sea exposure</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Capacity is finite and often already heavily utilised, so incremental relief can
                  be limited.
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Moving a subset of volumes via pipelines or shorter coastal routes where
                  infrastructure exists.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-600 dark:text-on-surface-variant">
          Figures are indicative and rounded; actual voyage distances and days depend on loading
          port, destination, vessel speed, and routing decisions at the time.
        </p>
      </div>

      {/* Navigation Details & Risk Linkages */}
      <div className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-4">
          Navigation Characteristics and Freight Sensitivity
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-medium text-slate-900 dark:text-on-surface mb-2">Channel width</h3>
            <p className="text-sm text-slate-600 dark:text-on-surface-variant">
              The narrowest point in the Strait of Hormuz is often cited at around 21 nautical
              miles, with traffic separation schemes that allocate just a few miles of navigable
              space in each direction. This concentration makes even routine traffic management a
              complex coordination exercise.
            </p>
          </div>
          <div>
            <h3 className="font-medium text-slate-900 dark:text-on-surface mb-2">Traffic volume</h3>
            <p className="text-sm text-slate-600 dark:text-on-surface-variant">
              On a typical day, dozens of crude, product, and LNG tankers transit the strait. When
              convoys or naval escorts are introduced, throughput can remain high but operational
              buffers shrink, leaving less margin for disruption.
            </p>
          </div>
          <div>
            <h3 className="font-medium text-slate-900 dark:text-on-surface mb-2">Freight and insurance</h3>
            <p className="text-sm text-slate-600 dark:text-on-surface-variant">
              Disruptions or heightened tensions can raise time‑charter equivalents and spot freight
              rates, particularly for vessel classes that specialise in Gulf loadings. War‑risk
              premia and re‑routing together can move delivered costs by double‑digit percentages
              even when benchmark oil prices move less.
            </p>
          </div>
          <div>
            <h3 className="font-medium text-slate-900 dark:text-on-surface mb-2">Link to country exposure</h3>
            <p className="text-sm text-slate-600 dark:text-on-surface-variant">
              Importers that rely heavily on Gulf cargoes – such as many in Asia – feel these
              routing changes most directly. For a complementary view by country, see the{' '}
              <Link
                to="/strait-of-hormuz/countries-dependent"
                className="text-primary-600 dark:text-primary underline"
              >
                Countries Dependent
              </Link>{' '}
              page, and for how routing risk feeds into overall scores, see the{' '}
              <Link
                to="/strait-of-hormuz/risk-dashboard"
                className="text-primary-600 dark:text-primary underline"
              >
                Risk Dashboard
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    </article>
  );
};

export default HormuzShippingRoutes;

